import { cn } from "@gettactic/helpers";
import { cva, type VariantProps } from "class-variance-authority";
import { ImageOff } from "lucide-react";
import React from "react";
import { Image } from "../Image/Image";

const imageThumbnailVariants = cva(
  "flex shrink-0 items-center justify-center overflow-hidden bg-neutral-100",
  {
    variants: {
      size: {
        /** Inline with a line of text, e.g. next to a select option's label. */
        xs: "h-5 w-5 rounded-sm",
        /** Compact square, e.g. inside a select trigger. */
        sm: "h-11 w-11 rounded-lg",
        /** Landscape preview, e.g. a row in an image picker. */
        lg: "h-20 w-28 rounded-lg"
      }
    },
    defaultVariants: {
      size: "sm"
    }
  }
);

const placeholderIconVariants = cva("text-neutral-300", {
  variants: {
    size: {
      xs: "h-3 w-3",
      sm: "h-4 w-4",
      lg: "h-5 w-5"
    }
  },
  defaultVariants: {
    size: "sm"
  }
});

export interface ImageThumbnailProps
  extends VariantProps<typeof imageThumbnailVariants> {
  /** Image source. A placeholder is shown when omitted. */
  src?: string;
  /**
   * Alternative text. Defaults to empty: a thumbnail sitting next to its own
   * label is decorative, and repeating the label just adds noise for screen
   * reader users.
   */
  alt?: string;
  className?: string;
}

/**
 * Fixed-size image preview with a placeholder fallback, used wherever a record
 * carries a picture that accompanies (rather than replaces) its label.
 */
export const ImageThumbnail: React.FC<ImageThumbnailProps> = ({
  src,
  alt = "",
  size,
  className
}) => (
  <span className={cn(imageThumbnailVariants({ size }), className)}>
    {src ? (
      <Image src={src} alt={alt} wrapperClassName="h-full w-full" />
    ) : (
      <ImageOff className={placeholderIconVariants({ size })} />
    )}
  </span>
);
